import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Loading } from 'components/Loading'
import { Spinner } from 'components/Spinner'
import PageNotFound from 'routes/PageNotFound/PageNotFound'
import { useApiClient } from 'utils/api/ApiClient'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { links, paymentProviderDetailsRouteUrl } from 'utils/featureToggles'

import { NPaymentProviderDto } from '../../../api/NPayments/Types/NPaymentProviderDto'
import { Box } from '../../../components/Box'
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal'
import { ToastContext } from '../../../components/Toast/Toast'
import { NPaymentsApi } from '../../../utils/api/nPaymentsApi'
import { PaymentProviderCountriesDetailsPage } from './PaymentProviderCountriesDetailsPage'
import { PaymentProviderLanguagesDetailsPage } from './PaymentProviderLanguagesDetailsPage'
import { PaymentProviderNavigation } from './PaymentProviderNavigation'
import { PaymentProviderOptionsAndLimitsDetailsPage } from './PaymentProviderOptionsAndLimitsDetailsPage'

export enum PaymentProviderTab {
  OPTIONS_AND_LIMIT = 'options-and-limit',
  COUNTRIES = 'countries',
  LANGUAGES = 'languages',
}

export const ResolvePaymentProviderSlug = (): JSX.Element => {
  const { id, slug } = useParams<{ id: string; slug: PaymentProviderTab }>()
  const nPaymentsApi = useApiClient(NPaymentsApi)
  const setToast = useContext(ToastContext)
  const [crumbs, setCrumbs] = useState<{ title: string; link: string }[]>([
    { title: 'Payment Providers Config', link: links.administrationPaymentProvidersConfig.url },
  ])
  const [isEdit, setEdit] = useState(false)
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false)
  const [navigationLink, setNavigationLink] = useState<string | null>(null)
  const navigate = useNavigate()

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(
    () => (id ? nPaymentsApi.getPaymentProviderDetailConfig(id) : Promise.resolve(null)),
    []
  )
  const { data, isLoading } = useFetchOne(fetchData)

  const matchesSlug = (tab?: string) => {
    if (!tab) {
      return false
    }
    return (
      tab === PaymentProviderTab.OPTIONS_AND_LIMIT ||
      tab === PaymentProviderTab.COUNTRIES ||
      tab === PaymentProviderTab.LANGUAGES
    )
  }

  const updatePspConfig = useCallback(async (config: NPaymentProviderDto) => {
    await nPaymentsApi.updatePaymentProviderDetailConfig(config)
    setToast({ type: 'success', title: 'Config has been updated.' })
    forceRefresh()
    setEdit(false)
  }, [])

  useEffect(() => {
    if (data) {
      const [crumb1, crumb2] = [
        ...crumbs,
        {
          title: data?.name || '',
          link: paymentProviderDetailsRouteUrl(data, PaymentProviderTab.OPTIONS_AND_LIMIT),
        },
      ]
      setCrumbs([crumb1, crumb2])
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleBreadcrumbClick = (link: string) => {
    if (isEdit) {
      setNavigationLink(link)
      setConfirmationModal(true)
    } else {
      navigate(link)
    }
  }

  const handleBreadcrumbNavigation = (shouldNavigate: boolean) => {
    if (shouldNavigate && navigationLink) {
      setEdit(false)
      navigate(navigationLink)
    }
    setConfirmationModal(false)
    setNavigationLink(null)
  }

  const innerRender = () => {
    if (!matchesSlug(slug)) {
      return <PageNotFound />
    }
    if (isLoading) {
      return <Spinner />
    }
    if (!data) {
      return <PageNotFound />
    }
    if (slug === PaymentProviderTab.OPTIONS_AND_LIMIT) {
      return (
        <React.Fragment>
          <Box display='flex' alignItems='baseline'>
            <Breadcrumbs crumbs={crumbs} onCrumbClick={handleBreadcrumbClick} />
            <Badge variant='outline-primary'>{data.enabled ? 'Enabled' : 'Disabled'}</Badge>{' '}
          </Box>
          <PaymentProviderNavigation isEdit={isEdit} setEdit={setEdit} />
          <PaymentProviderOptionsAndLimitsDetailsPage
            data={data}
            handleSubmit={updatePspConfig}
            isEdit={isEdit}
            setEdit={setEdit}
          />
        </React.Fragment>
      )
    }
    if (slug === PaymentProviderTab.COUNTRIES) {
      return (
        <React.Fragment>
          <Box display='flex' alignItems='baseline'>
            <Breadcrumbs crumbs={crumbs} onCrumbClick={handleBreadcrumbClick} />
            <Badge variant='outline-primary'>{data.enabled ? 'Enabled' : 'Disabled'}</Badge>
          </Box>
          <PaymentProviderNavigation isEdit={isEdit} setEdit={setEdit} />
          <PaymentProviderCountriesDetailsPage
            data={data}
            handleSubmit={updatePspConfig}
            isEdit={isEdit}
            setEdit={setEdit}
          />
        </React.Fragment>
      )
    }
    if (slug === PaymentProviderTab.LANGUAGES) {
      return (
        <React.Fragment>
          <Box display='flex' alignItems='baseline'>
            <Breadcrumbs crumbs={crumbs} onCrumbClick={handleBreadcrumbClick} />
            <Badge variant='outline-primary'>{data.enabled ? 'Enabled' : 'Disabled'}</Badge>
          </Box>
          <PaymentProviderNavigation isEdit={isEdit} setEdit={setEdit} />
          <PaymentProviderLanguagesDetailsPage
            data={data}
            handleSubmit={updatePspConfig}
            isEdit={isEdit}
            setEdit={setEdit}
          />
        </React.Fragment>
      )
    }
    return <PageNotFound />
  }

  return (
    <Loading isLoading={isLoading && !matchesSlug(slug)}>
      {innerRender()}
      <ConfirmationModal
        contentText='The page is asking you to confirm that you want to leave - data you have entered may not be saved.'
        yesButtonText='Leave Page'
        cancelButtonText='Stay on page'
        onYes={() => handleBreadcrumbNavigation(true)}
        onCancel={() => handleBreadcrumbNavigation(false)}
        visible={confirmationModal}
      />
    </Loading>
  )
}
